import React from "react";
import { Popover } from "antd";
import { ChevronDown } from "lucide-react";
import { ClockCard } from "../../../../modules/stealth-clock";
import { isStealthClockEnabledForOrg } from "../../../../modules/stealth-clock/orgGating";
import { getMyClock } from "../../../../modules/stealth-clock/api";

/**
 * ShiftChip — the shift clock as a header control instead of a page-wide strip.
 *
 * Why a chip and not the card: the graphical dashboard is org analytics. The
 * shift clock is the one PERSONAL thing on it, and as a full-width strip above
 * the hero it pushed the org headline down while occupying the most prominent
 * slot on the page for the narrowest-scope control. In the header it sits with
 * the other personal chrome (greeting, avatar), never scrolls away, and costs
 * the analytics nothing.
 *
 * The card itself is untouched — it renders whole inside the popover, so the
 * sessions list and day rail are one click away and `modules/stealth-clock`
 * needs no `compact` prop or any other change.
 *
 * Pressing Start/End is deliberately NOT on the chip. It is a twice-a-day action
 * with real consequences; ending someone's shift on a stray click in a toolbar
 * would be a bad trade for one saved click. Open, see your sessions, then act.
 */
export function ShiftChip({ t }) {
  const [open, setOpen] = React.useState(false);
  const [state, setState] = React.useState(null); // { on, staleOpen, sinceMs }
  const [hidden, setHidden] = React.useState(false);
  const mounted = React.useRef(true);

  React.useEffect(() => () => { mounted.current = false; }, []);

  const refresh = React.useCallback(async () => {
    // Same pre-gate the card uses: a disabled org issues ZERO requests.
    const profile = typeof window !== "undefined" ? window.__orgProfile : null;
    if (profile && !isStealthClockEnabledForOrg(profile)) {
      if (mounted.current) setHidden(true);
      return;
    }
    try {
      const res = await getMyClock();
      if (!mounted.current) return;
      if (!res || res.enabled !== true || res.isAgentUser !== true) { setHidden(true); return; }
      const openSeg = (res.segments || []).find((s) => s.open) || null;
      setHidden(false);
      setState({
        on: res.state === "in",
        staleOpen: res.stale_open === true,
        sinceMs: openSeg ? new Date(openSeg.from).getTime() : 0,
      });
    } catch (_e) {
      // Never render a broken control — hide exactly as if the feature were off.
      if (mounted.current) setHidden(true);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Live elapsed. Ticks only while a shift is open AND the tab is visible, and
  // recomputes from the start instant rather than accumulating, so it is exact
  // the moment the tab comes back without having burned cycles while hidden.
  const [, tick] = React.useState(0);
  React.useEffect(() => {
    if (!state?.on || !state.sinceMs) return undefined;
    const run = () => { if (document.visibilityState !== "hidden") tick((n) => n + 1); };
    const id = setInterval(run, 1000);
    return () => clearInterval(id);
  }, [state?.on, state?.sinceMs]);

  if (hidden || !state) return null;

  const elapsed = state.on && state.sinceMs
    ? Math.max(0, Math.floor((Date.now() - state.sinceMs) / 1000))
    : 0;
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const elapsedLabel = h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;

  // The dot leads and there is no glyph beside it. On this chip the STATE is the
  // content — a play/pause icon would only repeat what the colour and the words
  // already say, and it competed with the tinted glyphs on the chips to its left.
  //
  // Colour rides the WHOLE chip, not just a 6px dot. This is the one control in
  // the row whose state someone needs to catch without looking for it: whether
  // your shift is running, and whether one was left open overnight. A neutral
  // chip with a small green dot loses that at a glance, especially beside five
  // other neutral chips. Off-duty stays neutral on purpose — the resting state
  // is not news, so it earns no colour.
  const tone = state.staleOpen
    ? {
        dot: t.isDark ? "#F0B24A" : "#D97706",
        wash: t.isDark ? "rgba(230,184,77,.16)" : "rgba(217,119,6,.13)",
        line: t.isDark ? "rgba(230,184,77,.45)" : "rgba(217,119,6,.42)",
        ink: t.isDark ? "#F0B24A" : "#92400e",
      }
    : state.on
      ? {
          dot: t.isDark ? "#34d399" : "#10b981",
          wash: t.isDark ? "rgba(52,211,153,.15)" : "rgba(16,185,129,.13)",
          line: t.isDark ? "rgba(52,211,153,.42)" : "rgba(16,185,129,.40)",
          ink: t.isDark ? "#6EE7B7" : "#065F46",
        }
      : null; // off duty — plain chip, no tint

  const label = state.staleOpen
    ? "Shift left open"
    : state.on ? `On shift · ${elapsedLabel}` : "Off shift";

  return (
    <Popover
      trigger="click"
      open={open}
      // Refresh on close so the chip reflects a Start/End pressed inside the card.
      onOpenChange={(next) => { setOpen(next); if (!next) refresh(); }}
      placement="bottomRight"
      arrow={false}
      content={<div style={{ width: 460, maxWidth: "90vw" }}><ClockCard /></div>}
      overlayInnerStyle={{
        padding: 0, background: "transparent", border: "none", boxShadow: "none",
      }}
    >
      <button
        type="button"
        className={`tg-tool tg-action ${open ? "is-open" : ""}`}
        style={tone ? {
          background: tone.wash,
          borderColor: tone.line,
          color: tone.ink,
          fontWeight: 600,
        } : undefined}
        title={state.staleOpen
          ? "A shift was left open — open to close it"
          : state.on ? "You are on shift — open to end it" : "Open to start your shift"}
      >
        {/* A running shift borrows the page's own live pulse (tgwPing), the same
            signal used by the "Live" marker in the header — so "this is happening
            now" reads identically wherever it appears. A shift left open is NOT
            healthy-live, so it gets the amber dot without the pulse. */}
        {state.on && !state.staleOpen ? (
          <span className="tgw-live" style={{ width: 7, height: 7, background: tone.dot }} />
        ) : (
          <span
            className="tg-action-dot"
            style={{ background: tone ? tone.dot : t.c.textTertiary }}
          />
        )}
        <span className="tg-tool-value" style={{ fontVariantNumeric: "tabular-nums" }}>{label}</span>
        {/* The chevron is what makes this read as a control rather than a badge.
            Every other chip that opens a panel carries one; a coloured pill
            without it looks like a status readout you cannot press. */}
        <ChevronDown
          size={13}
          style={{
            opacity: .7,
            transition: "transform 160ms var(--tg-ease, ease)",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </button>
    </Popover>
  );
}

export default ShiftChip;
